<script lang="ts">
  import { link } from "svelte-spa-router";
  import { entries, bySlug, relatedSops } from "../lib/content";
  import { categoryById } from "../lib/categories";
  import { renderMarkdown, extractToc } from "../lib/markdown";
  import { favourites, readSet } from "../lib/stores";
  import { isSignedIn } from "../lib/adminStore";
  import DriveEmbed from "../lib/components/DriveEmbed.svelte";
  import DocLinkCard from "../lib/components/DocLinkCard.svelte";
  import TableOfContents from "../lib/components/TableOfContents.svelte";
  import SopCard from "../lib/components/SopCard.svelte";

  export let params: { slug: string } = { slug: "" };

  $: sop = bySlug($entries, params.slug);
  $: category = sop ? categoryById(sop.category) : undefined;
  $: html = sop ? renderMarkdown(sop.body) : "";
  $: toc = sop ? extractToc(sop.body) : [];
  $: related = sop ? relatedSops($entries, sop, 3) : [];
  $: starred = sop ? $favourites.has(sop.slug) : false;
  $: read = sop ? $readSet.has(sop.slug) : false;

  function fmtDate(s: string): string {
    if (!s) return "";
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }
</script>

{#if !sop}
  <div class="panel p-8 text-center">
    <h1 class="text-xl font-semibold mb-2">Not found</h1>
    <p class="text-text-muted mb-4">No SOP exists at this slug.</p>
    <a use:link href="/" class="btn">Back to playbook</a>
  </div>
{:else}
  <div class="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-8">
    <article class="min-w-0 flex flex-col gap-6">
      <header class="flex flex-col gap-3">
        <div class="flex items-center gap-2 text-sm text-text-muted">
          <a use:link href="/" class="hover:text-text">Playbook</a>
          {#if category}
            <span>/</span>
            <a use:link href={`/category/${category.id}`} class="hover:text-text">
              {category.label}
            </a>
          {/if}
        </div>
        <h1 class="text-3xl font-semibold tracking-tight">{sop.title}</h1>
        {#if sop.description}
          <p class="text-text-muted text-lg">{sop.description}</p>
        {/if}
        <div class="flex flex-wrap items-center gap-3 text-xs text-text-dim">
          {#if sop.status === "stub"}
            <span class="pill text-text-dim">Coming soon</span>
          {:else if sop.status === "draft"}
            <span class="pill text-warn border-warn/40">Draft</span>
          {/if}
          <span>by {sop.owner}</span>
          {#if sop.updated}
            <span>·</span>
            <span>updated {fmtDate(sop.updated)}</span>
          {/if}
          {#if sop.estimatedMinutes}
            <span>·</span>
            <span>{sop.estimatedMinutes} min read</span>
          {/if}
          {#each sop.tags as tag}
            <span class="pill text-text-muted text-[10px]">#{tag}</span>
          {/each}
        </div>

        <div class="flex flex-wrap items-center gap-2 pt-2">
          <button
            class="btn"
            on:click={() => sop && favourites.toggle(sop.slug)}
          >
            {starred ? "★ Starred" : "☆ Star"}
          </button>
          <button
            class="btn"
            class:btn-primary={!read}
            on:click={() => sop && readSet.toggle(sop.slug)}
          >
            {read ? "✓ Marked as read" : "Mark as read"}
          </button>
          {#if $isSignedIn && sop}
            <a use:link href={`/admin/edit/${sop.slug}`} class="btn">
              ✎ Edit
            </a>
          {/if}
        </div>
      </header>

      {#if sop.video}
        <DriveEmbed src={sop.video} />
      {/if}

      {#if sop.docs.length > 0}
        <section class="flex flex-col gap-2">
          <h2 class="text-xs uppercase tracking-wider text-text-dim">Linked documents</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {#each sop.docs as doc}
              <DocLinkCard {doc} />
            {/each}
          </div>
        </section>
      {/if}

      <div class="md-body">
        {@html html}
      </div>

      {#if related.length > 0}
        <section class="pt-4 border-t border-border flex flex-col gap-3">
          <h2 class="text-xs uppercase tracking-wider text-text-dim">Related</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {#each related as r (r.slug)}
              <SopCard sop={r} />
            {/each}
          </div>
        </section>
      {/if}
    </article>

    <aside class="hidden lg:block">
      <TableOfContents entries={toc} />
    </aside>
  </div>
{/if}
